<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import TimePeriodV2 from '@/common/components/molecules/timePeriod/TimePeriodV2.vue';
import type {
  PaTimePeriodFilterOptions,
  ReadOnlyTimes,
  TimePeriodData,
} from '@/types/common.types';
import { useInternational } from '@/common/locale';
import { getLocaleKey } from '@/common/utils/commonUtils';
import type { TimePeriodInfo } from '../types';
import type { TimezoneName } from '@/common/utils';
import {
  formatTimeRangeToUtc,
  getFormalPeriod,
  getRangeDisplayTime,
  getLiveDisplayTime,
} from '../utils';
import { CUSTOM_PERIOD_PREFIX, DEFAULT_PERIOD_ITEMS } from '../constants';

interface Props {
  modelValue: TimePeriodInfo;
  items?: Readonly<TimePeriodData[]>;
  maxRangeSec?: number;
  minRangeSec?: number;
  displayDateOnly?: boolean;
  timeUnit?: 'day' | 'sec';
  hideDropdownWrapper?: boolean;
  useInput?: boolean;
  disabled?: boolean;
  isError?: boolean;
  /** Live 업데이트 사용 여부. false이면 항상 range 포맷 사용 */
  useLiveUpdater?: boolean;
  readonlyTimes?: ReadOnlyTimes;

  paTimeFilterOptions?: PaTimePeriodFilterOptions;
  calendarPos?: 'left' | 'right';
  /** 시간 계산·표시 기준 타임존 */
  timezone?: TimezoneName;
}

interface Emits {
  (e: 'update:modelValue', value: TimePeriodInfo): void;
  (e: 'select', range: { fromTime: string; toTime: string }): void;
  (e: 'change-period'): void;
  (
    e: 'change-period-by-calendar',
    payload: { fromTime: string; toTime: string; period: string },
  ): void;
  (e: 'dropdown-open'): void;
}

const props = withDefaults(defineProps<Props>(), {
  items: () => DEFAULT_PERIOD_ITEMS,
  maxRangeSec: undefined,
  minRangeSec: undefined,
  displayDateOnly: false,
  timeUnit: undefined,
  hideDropdownWrapper: false,
  useInput: false,
  disabled: false,
  useLiveUpdater: true,
  readonlyTimes: undefined,
  paTimeFilterOptions: () => ({}),
  calendarPos: 'left',
  timezone: undefined,
});

const emit = defineEmits<Emits>();

// ============================================================
// State
// ============================================================

const { t } = useInternational();
const timePeriodV2Ref = ref<InstanceType<typeof TimePeriodV2> | null>(null);

const periodItems = ref<TimePeriodData[]>(
  props.items.map<TimePeriodData>(({ name, value, shortName }) => ({
    name: t(`WORD.TIME_PERIOD_ITEMS.${getLocaleKey(name)}`),
    value,
    shortName,
  })),
);

const selectedPeriod = ref<string>(props.modelValue.timePeriod || 'p10m');

// ============================================================
// Computed
// ============================================================

const isPeriodItem = computed(() => {
  const period = getFormalPeriod(selectedPeriod.value);
  const periodValues = periodItems.value.map(({ value }) => value);
  return periodValues.includes(period);
});

const isShowDropdown = computed(() => timePeriodV2Ref.value?.isShowDropdown ?? false);

// ============================================================
// Custom Period Management
// ============================================================

const updateCustomPeriodItem = ({
  period,
  displayTime,
}: {
  period: string;
  displayTime: string;
}) => {
  const periodItem = {
    name: displayTime,
    value: `${CUSTOM_PERIOD_PREFIX}${period}`,
    shortName: period.split(' ')[0].slice(1),
  };

  const customPeriodIdx = periodItems.value.findIndex(({ value }) =>
    value.includes(CUSTOM_PERIOD_PREFIX),
  );
  if (customPeriodIdx > -1) {
    periodItems.value[customPeriodIdx] = periodItem;
  } else {
    periodItems.value.unshift(periodItem);
  }

  selectedPeriod.value = periodItems.value[0].value;
};

// ============================================================
// Handlers
// ============================================================

const handleChangePeriod = () => {
  // 동일 기간 선택한 경우에는 무시 (custom 선택)
  if (selectedPeriod.value.includes(CUSTOM_PERIOD_PREFIX)) {
    return;
  }

  emit('change-period');
};

const handleChangePeriodByCalendar = ({
  fromTime,
  toTime,
  period,
}: {
  fromTime: string;
  toTime: string;
  period: string;
}) => {
  emit('update:modelValue', {
    timePeriod: period,
    fromTime,
    toTime,
    ...formatTimeRangeToUtc({ fromTime, toTime, timezone: props.timezone }),
    isPaused: true,
  });

  updateCustomPeriodItem({
    period,
    displayTime: getRangeDisplayTime({
      fromTime,
      toTime,
      dateOnly: props.displayDateOnly,
    }),
  });

  emit('select', { fromTime, toTime });
  emit('change-period-by-calendar', { fromTime, toTime, period });
};

// ============================================================
// Watchers
// ============================================================

// modelValue 변경 시 period item 업데이트
watch(
  () => props.modelValue,
  (info) => {
    const { timePeriod, fromTime, toTime, isPaused } = info;

    // 외부에서 변경된 경우 custom period item 업데이트
    // useLiveUpdater가 false면 항상 range 포맷 사용
    const useRangeFormat = !props.useLiveUpdater || isPaused || timePeriod === 'pa';

    updateCustomPeriodItem({
      period: getFormalPeriod(timePeriod),
      displayTime: useRangeFormat
        ? getRangeDisplayTime({
            fromTime,
            toTime,
            dateOnly: props.displayDateOnly,
          })
        : getLiveDisplayTime({ toTime }),
    });
  },
  { deep: true, immediate: true },
);

// items prop 변경 시 periodItems 업데이트
watch(
  () => props.items,
  (newItems) => {
    periodItems.value = newItems.map<TimePeriodData>(({ name, value, shortName }) => ({
      name: t(`WORD.TIME_PERIOD_ITEMS.${getLocaleKey(name)}`),
      value,
      shortName,
    }));
  },
  { deep: true },
);

// ============================================================
// Expose
// ============================================================

defineExpose({
  selectedPeriod,
  periodItems,
  isPeriodItem,
  isShowDropdown,
  updateCustomPeriodItem,
});
</script>

<template>
  <time-period-v2
    ref="timePeriodV2Ref"
    v-model="selectedPeriod"
    :disabled="props.disabled"
    :is-error="props.isError"
    :periods="periodItems"
    :from-time="props.modelValue.fromTime"
    :to-time="props.modelValue.toTime"
    :time-range-sec-limit="props.maxRangeSec"
    :min-time-range-sec-limit="props.minRangeSec"
    :time-unit="props.timeUnit"
    :hide-dropdown-wrapper="props.hideDropdownWrapper"
    :use-calendar="true"
    :use-input="props.useInput"
    :readonly-times="props.readonlyTimes"
    :pa-time-filter-options="props.paTimeFilterOptions"
    :calendar-pos="props.calendarPos"
    :timezone="props.timezone"
    @change-period="handleChangePeriod"
    @change-period-by-calendar="handleChangePeriodByCalendar"
    @dropdown-open="emit('dropdown-open')"
  />
</template>
